import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, num, fmtDate, downloadFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Eye, FileSpreadsheet, FileText, Trash2, BarChart3 } from "lucide-react";

const statusColor = (s) =>
  ({ Completed: "bg-ok text-ok-foreground border-green-200",
     "Needs Review": "bg-warn text-warn-foreground border-warn-border",
     Failed: "bg-red-50 text-red-700 border-red-200",
     Processing: "bg-secondary text-secondary-foreground border-blue-200" }[s] || "bg-muted text-muted-foreground");

export default function Documents() {
  const [params] = useSearchParams();
  const [docs, setDocs] = useState([]);
  const [q, setQ] = useState(params.get("q") || "");
  const nav = useNavigate();

  const load = async () => {
    try { const { data } = await api.get("/documents"); setDocs(data); }
    catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    try { await api.delete(`/documents/${id}`); toast.success("Document deleted"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const filtered = docs.filter((d) => d.filename.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-6" data-testid="documents-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">History</p>
          <h1 className="mt-2 font-display text-3xl font-extrabold tracking-tighter">Documents</h1>
        </div>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by filename…"
               className="w-64 bg-white" data-testid="documents-filter-input" />
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-lg border border-border bg-white p-12 text-center" data-testid="documents-empty">
          <p className="text-sm text-muted-foreground">No documents found.</p>
          <Button className="mt-4 rounded-full" onClick={() => nav("/analyzer")} data-testid="documents-empty-cta">
            Analyze a statement
          </Button>
        </div>
      ) : (
        <div className="grid gap-4">
          {filtered.map((d) => (
            <div key={d.doc_id} className="flex flex-wrap items-center gap-4 rounded-lg border border-border bg-white p-4"
                 data-testid={`document-card-${d.doc_id}`}>
              <div className="min-w-[220px] flex-1">
                <p className="truncate font-medium">{d.filename}</p>
                <p className="text-xs text-muted-foreground">
                  {d.doc_type === "bank_statement" ? "Bank Statement" : "Image → Excel"} · uploaded {fmtDate(d.uploaded_at)}
                  {d.pages ? ` · ${d.pages} page(s)` : ""}
                </p>
              </div>
              <Badge variant="outline" className={statusColor(d.status)}>{d.status}</Badge>
              <span className="num text-sm">{num(d.transaction_count)} txns</span>
              <Badge variant="outline">{d.analysis_status}</Badge>
              <div className="flex gap-1">
                <Button size="icon" variant="ghost" title="View / Analyze" onClick={() => nav(`/workspace/${d.job_id}`)}
                        data-testid={`doc-view-${d.doc_id}`}><Eye className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" title="Analyze" onClick={() => nav(`/workspace/${d.job_id}`)}
                        data-testid={`doc-analyze-${d.doc_id}`}><BarChart3 className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" title="Export Excel"
                        onClick={() => downloadFile(`/export/excel/${d.job_id}`, "GrowRupee.xlsx")
                          .then(() => toast.success("Excel exported")).catch((e) => toast.error(errMsg(e)))}
                        data-testid={`doc-excel-${d.doc_id}`}><FileSpreadsheet className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" title="Generate Report"
                        onClick={() => downloadFile(`/export/pdf/${d.job_id}`, "GrowRupee_Report.pdf")
                          .then(() => toast.success("Report generated")).catch((e) => toast.error(errMsg(e)))}
                        data-testid={`doc-pdf-${d.doc_id}`}><FileText className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" title="Delete" onClick={() => del(d.doc_id)}
                        data-testid={`doc-delete-${d.doc_id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
